import React, { useState, useEffect } from 'react';
import { Table, Label, Row, Col, Spinner } from 'reactstrap';
import DollarInput from '../../../common/DollarInput';
import DecimalInput from '../../../common/DecimalInput';
import { setHomeLoanResultsData } from '../../../../ReduxToolkit/Reducers/HomeResultsSlice';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../ReduxToolkit/Store';
import { useGetRequestQuery } from '../../../../Hooks/useGetRequestQuery';
import { getAssetsPropertyByClientIdService } from '../../../../Services/PublicPageServices';
import { H3 } from '../../../../AbstractElements';
import Dropdown from '../../../common/DropDown';
import { addUpdateHomeLoanResultsService, getHomeLoanResultsService } from '../../../../Services/MMPClientServices/YourWhy';
import { useMutationRequestQuery } from '../../../../Hooks/useMutationRequestQuery';

type LoanData = {
  month: string;
  openingBalance: number;
  closingBalance: number;
  masteryBalance: number;
  savings: number;
  accumulatedSavings: number;
  budgetedSavings: number;
  extraPayments: number;
};

const MortgageCalculator: React.FC = () => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const dispatch = useDispatch();
  const [selectedMonths, setSelectedMonths] = useState(24);
  const dashboardData = useSelector((state: any) => state.dashboard.dashboardData);

  const { loanAmount, interestRate, loanTerm, monthlyRepayment } = useSelector(
    (state: RootState) => state.homeLoanResults
  );

  // Convert string values to numbers
  const loanAmountNum = Number(loanAmount.replace(/,/g, ''));
  const interestRateNum = Number(interestRate.replace(/,/g, ''));
  const monthlyRepaymentNum = Number(monthlyRepayment.replace(/,/g, ''));
  const [loanData, setLoanData] = useState<LoanData[]>([]);
  const [assetsPropertyData, setAssetsPropertyData] = useState<any>(null);
  const userData = useSelector((state: RootState) => state.auth);

  const {
    data: assetsProperty,
    refetch: refetchAssetsProperty,
    isLoading: assetsPropertyLoading,
    isError: assetsPropertyIsError,
  } = useGetRequestQuery(
    ['getAssetsPropertyByClientId', userData?.nameidentifier],
    () => getAssetsPropertyByClientIdService(userData?.nameidentifier as any),
    {
      enabled: true,
    }
  );

  useEffect(() => {
    if (assetsProperty?.data && !assetsPropertyIsError && !assetsPropertyLoading) {
      const ownerOccupiedProperties = assetsProperty.data.filter(
        (property: any) => property.propertyUse === 'Owner Occupied'
      );
      setAssetsPropertyData(ownerOccupiedProperties[0]);
    }
  }, [assetsProperty, assetsPropertyIsError, assetsPropertyLoading]);

  const { data: homeLoanResults, refetch, isLoading, isError } = useGetRequestQuery(
    ['getHomeLoanResults', userData?.nameidentifier],
    () => getHomeLoanResultsService(userData?.nameidentifier as any),
    {
      enabled: !!userData?.nameidentifier,
    }
  );

  useEffect(() => {
    if (homeLoanResults?.data && !isError && !isLoading) {
      const formattedData = homeLoanResults?.data?.map((data: any) => ({
        month: data.monthYear,
        openingBalance: Number(data.openingBalance) || 0,
        closingBalance: Number(data.closingBalance) || 0,
        masteryBalance: Number(data.months) || 0,
        savings: Number(data.thisMonth) || 0,
        accumulatedSavings: Number(data.accumulatedSavings) || 0,
        budgetedSavings: Number(data.budgetedSavings) || 0,
        extraPayments: Number(data.extraPayments) || 0,
      }));

      setLoanData(formattedData);
      setSelectedMonths(homeLoanResults?.data[0]?.totalMonthSelected || 24)
    }
  }, [homeLoanResults, isLoading, isError]);

  const { mutate: updateHomeLoanResults } = useMutationRequestQuery(
    'updateHomeLoanResults',
    addUpdateHomeLoanResultsService,
    {
      onSuccess: () => {
        refetch();
      },
      onError: () => {
      },
    }
  );

  useEffect(() => {
    if (loanAmount && interestRate && monthlyRepayment) {

      let existingData = loanData || [];
      let tempData: LoanData[] = [...existingData];
      let balance = existingData.length > 0 ? existingData[existingData.length - 1].closingBalance : loanAmountNum;
      let existingMonthsCount = existingData.length;
      let monthsToAdd = selectedMonths - existingMonthsCount;

      for (let i = existingMonthsCount; i < selectedMonths; i++) {
        const date = new Date(currentYear, currentMonth + i, 1);
        const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' });
        const interest = (balance * (interestRateNum / 100)) / 12;
        const closingBalance = balance - (monthlyRepaymentNum - interest);

        tempData.push({
          month: monthYear,
          openingBalance: balance,
          closingBalance: closingBalance < 0 ? 0 : closingBalance,
          masteryBalance: 0,
          savings: 0,
          accumulatedSavings: 0,
          budgetedSavings: dashboardData[5]?.amount || 0,
          extraPayments: 0,
        });

        balance = closingBalance < 0 ? 0 : closingBalance;
      }

      if (homeLoanResults?.data?.length === 0 || selectedMonths > homeLoanResults?.data?.length) {
        updateHomeLoanResults(
          tempData.map((data: any) => ({
            clientID: userData?.nameidentifier,
            months: data.masteryBalance.toString(),
            openingBalance: data.openingBalance.toString(),
            closingBalance: data.closingBalance.toString(),
            budgetedSavings: dashboardData[5]?.amount?.toString(),
            extraPayments: data.extraPayments.toString(),
            thisMonth: data.savings.toString(),
            accumulatedSavings: data.accumulatedSavings.toString(),
            totalMonthSelected: selectedMonths.toString(),
            monthYear: data.month.toString(),
          }))
        );
      }
    } else {
    }
  }, [loanAmountNum, interestRateNum, monthlyRepaymentNum, selectedMonths]);



  const handleMasteryBalanceChange = (index: number, value: string) => {
    let updatedData = [...loanData];
    let masteryBalance = Number(value) || 0;
    let closingBalance = updatedData[index].closingBalance;
    let budgetedSavings = updatedData[index].budgetedSavings;

    updatedData[index].masteryBalance = masteryBalance;

    updatedData[index].savings = masteryBalance - closingBalance;

    updatedData[index].accumulatedSavings =
      index > 0 ? updatedData[index - 1].accumulatedSavings + updatedData[index].savings : updatedData[index].savings;
    updatedData[index].extraPayments = (-updatedData[index].savings) - budgetedSavings;

    setLoanData(updatedData);
  };

  const handleBlur = () => {
    updateHomeLoanResults(
      loanData.map((data) => ({
        clientID: userData?.nameidentifier,
        months: data.masteryBalance.toString(),
        openingBalance: data.openingBalance.toString(),
        closingBalance: data.closingBalance.toString(),
        budgetedSavings: dashboardData[5]?.amount?.toString() || "0",
        extraPayments: data.extraPayments.toString(),
        thisMonth: data.savings.toString(),
        accumulatedSavings: data.accumulatedSavings.toString(),
        totalMonthSelected: selectedMonths.toString(),
        monthYear: data.month.toString(),
      }))
    );
  };


  const handleValueChange = (name: string, value: string | Date | null) => {
    dispatch(setHomeLoanResultsData({ ...{ loanAmount, interestRate, loanTerm, monthlyRepayment }, [name]: value }));
  };

  useEffect(() => {
    dispatch(
      setHomeLoanResultsData({
        loanAmount: assetsPropertyData?.currentMortgage || "",
        interestRate: assetsPropertyData?.interestRate || "",
        monthlyRepayment: assetsPropertyData?.loanDetailsMonthlyRepayment || "",
        loanTerm: loanTerm || "",
      })
    );
  }, [assetsPropertyData]);

  return (
    <div className='container-fluid'>
      <div className='table-responsive h-100 py-4'>
        <section className='bg-white'>
          <div className='scrollable-table-container'>
            <table className='scrollable-table' style={{ marginTop: '3%' }}>
              <div className="d-flex justify-content-between align-items-center">
                <H3 className="heading-color f-w-900">Home Loan Results</H3>
              </div>

              <Row className='mt-4'>
                <div className='d-flex mt-2 flex-wrap'>
                  <Col xs={12} style={{ paddingInline: '2px', width: '10%' }}>
                    <Label className='f-w-900'>Select Months</Label>

                    <Dropdown
                      label=""
                      name="selectedMonths"
                      value={selectedMonths?.toString() || '24'}
                      setValue={(name: any, value: any) => setSelectedMonths((value))}

                      // setValue={handleValueChange}
                      passCol="col-10"
                      design="flex"
                      // error={formErrors.storey}
                      options={['12', '24', '36', '48', '60', '72', '84', '96']}
                      errorMessage="Month is required!"
                    />
                  </Col>
                  <Col xs={12} style={{ paddingInline: '2px', width: '10%' }}>
                    <Label className='f-w-900'>Loan Amount</Label>
                    <DollarInput
                      label=''
                      name='loanAmount'
                      value={loanAmount}
                      setValue={handleValueChange}
                      placeholder=''
                      design='default'
                      limit={7}
                      disabled
                      tabIndex={1}
                    />
                  </Col>
                  <Col xs={12} md={2} style={{ paddingInline: '2px', width: '10%' }}>
                    <Label className='f-w-900'>Interest Rate</Label>
                    <DecimalInput
                      label=''
                      name='interestRate'
                      value={interestRate}
                      setValue={handleValueChange}
                      placeholder=''
                      design='default'
                      tabIndex={2}
                      disabled
                      limit={4}
                    />
                  </Col>
                  <Col xs={12} md={2} style={{ paddingInline: '2px', width: '13%' }}>
                    <Label className='f-w-900'>Monthly Repayment</Label>
                    <DollarInput
                      label=''
                      name='monthlyRepayment'
                      value={monthlyRepayment}
                      setValue={handleValueChange}
                      placeholder=''
                      design='default'
                      limit={7}
                      tabIndex={3}
                      disabled
                    />
                  </Col>
                </div>
              </Row>

              {loanData.length !== 0 && assetsPropertyData?.currentMortgage && !isLoading && (
                <Table bordered className='mb-3' style={{ width: '75%' }} onBlur={handleBlur}>
                  <thead>
                    <tr>
                      <th className="f-w-900" colSpan={4} style={{ textAlign: "left", backgroundColor: "#f8f9fa" }}>OLD LOAN</th>
                      <th className="f-w-900" style={{ textAlign: "left", backgroundColor: "#f8f9fa", color: "#0000ff" }}>MORTGAGE MASTERY</th>
                      <th className="f-w-900" colSpan={2} style={{ textAlign: "left", backgroundColor: "#f8f9fa", color: "green" }}>YOUR SAVINGS</th>
                      <th className="f-w-900" colSpan={2} style={{ textAlign: "left", backgroundColor: "#f8f9fa", color: "green" }}></th>
                    </tr>
                    <tr>
                      <th className="f-w-900" colSpan={2} style={{ textAlign: "left", width: "220px" }}>Loan <br /> Month</th>
                      <th className="f-w-900" style={{ textAlign: "left" }}>Opening Balance</th>
                      <th className="f-w-900" style={{ textAlign: "left" }}>Closing Balance</th>
                      <th className="f-w-900" style={{ textAlign: "left", color: "#0000ff" }}>Actual <br /> Balance</th>
                      <th className="f-w-900" style={{ textAlign: "left", color: "#17a2b8" }}>Budgeted Savings</th>
                      <th className="f-w-900" style={{ textAlign: "left", color: "#dc3545" }}>Extra Payments</th>
                      <th className="f-w-900" style={{ textAlign: "left", color: "green" }}>This Month</th>
                      <th className="f-w-900" style={{ textAlign: "left", color: "green" }}>Accumulated Savings</th>

                    </tr>
                  </thead>
                  <tbody>
                    {loanData.map((data, index) => (
                      <tr key={index}>
                        <td style={{ color: 'blue', width: "220px" }} colSpan={2}>{data.month}</td>
                        <td>{Number(data.openingBalance.toFixed(0)).toLocaleString()}</td>
                        <td>{Number(data.closingBalance.toFixed(0)).toLocaleString()}</td>
                        <td>
                          <div style={{ height: '35px', overflow: 'hidden' }}>
                            <DollarInput
                              label=""
                              name="masteryBalance"
                              value={data?.masteryBalance ? Number(data.masteryBalance).toLocaleString() : ""}
                              setValue={(name, value) => handleMasteryBalanceChange(index, value.replace(/,/g, ''))}
                              placeholder=""
                              design="default"
                              limit={7}
                            />
                          </div>
                        </td>
                        {/* New columns */}
                        <td>{Number(data.budgetedSavings).toLocaleString()}</td>
                        <td>{Number(data.extraPayments.toFixed(0)).toLocaleString()}</td>
                        <td style={{ color: 'green', fontWeight: 'bold' }}>
                          {Number(Math.abs(data.savings).toFixed(0)).toLocaleString()}
                        </td>
                        <td style={{ color: 'green', fontWeight: 'bold' }}>
                          {Number(Math.abs(data.accumulatedSavings).toFixed(0)).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </Table>
              )}
              {isLoading && <div className="flex items-center text-center my-5 justify-center w-screen h-screen">
                <Spinner color="primary" size="lg" />
              </div>}
              {!isLoading && !assetsPropertyData?.currentMortgage && (<div className="flex items-center text-center my-5 justify-center w-screen h-screen">
                <h3 className="text-gray-500 text-xl">No data available for home loan result calculation</h3>
              </div>)}
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MortgageCalculator;
